import type { Breakpoint } from '@mui/material/styles';
import type { NavSectionProps } from 'src/components/nav-section';

import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { hideScrollY, varAlpha } from 'src/theme/styles';

import { Logo } from 'src/components/logo';
import { NavSectionMini, NavSectionVertical } from 'src/components/nav-section';
import { Scrollbar } from 'src/components/scrollbar';
import { usePathname } from 'src/routes/hooks';

import { NavToggleButton } from '../components/nav-toggle-button';

// ----------------------------------------------------------------------

export type NavVerticalProps = NavSectionProps & {
  isNavMini: boolean;
  layoutQuery: Breakpoint;
  onToggleNav: () => void;
  useNavMini?: boolean;
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
};

export function NavVertical({
  sx,
  data,
  slots,
  isNavMini,
  layoutQuery,
  onToggleNav,
  useNavMini,
  ...other
}: NavVerticalProps) {
  const theme = useTheme();
  const pathname = usePathname();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    const timer = window.setTimeout(() => {
      const active = container.querySelector<HTMLElement>('.state--active');
      if (!active) return;

      const containerRect = container.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const isVisible =
        activeRect.top >= containerRect.top && activeRect.bottom <= containerRect.bottom;

      if (!isVisible) {
        active.scrollIntoView({ block: 'center' });
      }
    }, 80);

    return () => window.clearTimeout(timer);
  }, [pathname, isNavMini]);

  const renderNavVertical = (
    <>
      {slots?.topArea ?? (
        <Box sx={{ pl: 3.5, pt: 2.5, pb: 1 }}>
          <Logo />
        </Box>
      )}

      <Scrollbar ref={scrollRef} fillContent>
        <NavSectionVertical data={data} sx={{ px: 2, flex: '1 1 auto' }} {...other} />

        {
          slots?.bottomArea
          // ?? <NavUpgrade />
        }
      </Scrollbar>
    </>
  );

  const renderNavMini = (
    <>
      {slots?.topArea ?? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
          <Logo />
        </Box>
      )}

      <NavSectionMini
        data={data}
        sx={{ pb: 2, px: 0.5, ...hideScrollY, flex: '1 1 auto', overflowY: 'auto' }}
        {...other}
      />

      {slots?.bottomArea}
    </>
  );

  return (
    <Box
      sx={{
        top: 0,
        left: 0,
        height: 1,
        display: 'none',
        position: 'fixed',
        flexDirection: 'column',
        bgcolor: 'var(--layout-nav-bg)',
        zIndex: 'var(--layout-nav-zIndex)',
        width: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
        borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
        transition: theme.transitions.create(['width'], {
          easing: 'var(--layout-transition-easing)',
          duration: 'var(--layout-transition-duration)',
        }),
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'flex',
        },
        ...sx,
      }}
    >
      {useNavMini && (
        <NavToggleButton
          isNavMini={isNavMini}
          onClick={onToggleNav}
          sx={{
            display: 'none',
            [theme.breakpoints.up(layoutQuery)]: {
              display: 'inline-flex',
            },
          }}
        />
      )}
      {isNavMini ? renderNavMini : renderNavVertical}
    </Box>
  );
}
